import { FC, useMemo, useState } from 'react';
import { Gray10, Gray100, Select, typeCompact14Regular, typeCompact24Display } from '@traceair/tds';
import { classes, media, style, stylesheet } from 'typestyle';
import { useDocumentsStore } from '../../../store/documentsStore';
import { CoaDocCard, DocCard, NewDocCard } from './DocCards';
import { DocType } from '../../../types';
import AddDocumentButton from './AddDocumentButton';
import { BREAKPOINT_SMALL, MOBILE_CONTAINER_PADDINGS } from '../../layout/styles';
import { Header } from '../../layout/header/Header';
import { FullscreenLoader } from '../../misc/FullscreenLoader';

const DocumentsWindow: FC = () => {
  const isInitialFetching = useDocumentsStore((state) => state.isInitialFetching);
  const goodDocs = useDocumentsStore((state) => state.goodDocs);
  const goodCoaDocs = useDocumentsStore((state) => state.goodCoaDocs);
  const needAttentionDocs = useDocumentsStore((state) => state.needAttentionDocs);
  const missedDocTypes = useDocumentsStore((state) => state.missedDocTypes);
  const needAttentionCoaDocs = useDocumentsStore((state) => state.needAttentionCoaDocs);

  const [filterByDocType, setFilterByDocType] = useState<DocType>();
  const docTypesAvailable: DocType[] = useMemo(() => {
    const types = [...new Set(goodDocs.map((d) => d.type))];

    if (goodCoaDocs.length > 0) types.push(DocType.CERTIFICATE_OF_AUTHORIZATION);

    return types;
  }, [goodDocs, goodCoaDocs]);

  const shouldShowNeedAttentionDocs =
    needAttentionDocs.length > 0 || missedDocTypes.length > 0 || needAttentionCoaDocs.length > 0;
  const goodDocsToShow = filterByDocType ? goodDocs.filter(({ type }) => type === filterByDocType) : goodDocs;
  const goodCoaDocsToShow =
    filterByDocType === DocType.CERTIFICATE_OF_AUTHORIZATION || !filterByDocType ? goodCoaDocs : [];

  const shouldShowTypeSelect = goodDocs.length > 0 || goodCoaDocs.length > 0;
  const shouldShowGoodDocs = goodDocsToShow.length > 0 || goodCoaDocsToShow.length > 0;

  return (
    <div className={css.documentsContainer}>
      {isInitialFetching ? (
        <FullscreenLoader />
      ) : (
        <>
          <Header className={css.headerContainer} heading="Documents">
            <div className={css.headerButtonsContainer}>
              {shouldShowTypeSelect && (
                <Select
                  placeholder="Show all documents"
                  label="Document type"
                  value={filterByDocType}
                  options={[
                    { label: 'Show all documents', value: 'ALL' },
                    ...docTypesAvailable.map((type) => ({ label: type, value: type })),
                  ]}
                  onChange={(value) => setFilterByDocType(value === 'ALL' ? undefined : (value as DocType))}
                  className={css.selectInput}
                />
              )}
              <AddDocumentButton />
            </div>
          </Header>

          <div className={css.containerInner}>
            <div className={contentResponsiveContainer}>
              <div className={css.content}>
                {shouldShowNeedAttentionDocs && (
                  <div className={css.needAttention}>
                    <div style={{ marginBottom: 24 }}>
                      <div className={typeCompact24Display}>Need attention</div>
                      <div className={classes(typeCompact14Regular, css.needAttentionSubtitle)}>
                        Please update these documents to continue flying
                      </div>
                    </div>
                    <div className={css.cardsContainer}>
                      {missedDocTypes.map((docType, idx) => (
                        <NewDocCard key={idx} docType={docType} />
                      ))}
                      {needAttentionCoaDocs.map((doc, idx) => (
                        <CoaDocCard key={idx} coaDocument={doc} isNeedAttention />
                      ))}
                      {needAttentionDocs.map((doc, idx) => (
                        <DocCard key={idx} document={doc} isNeedAttention />
                      ))}
                    </div>
                  </div>
                )}
                {shouldShowGoodDocs && (
                  <div className={css.cardsContainer}>
                    {goodCoaDocsToShow.map((doc, idx) => (
                      <CoaDocCard coaDocument={doc} key={idx} />
                    ))}
                    {goodDocsToShow.map((doc, idx) => (
                      <DocCard document={doc} key={idx} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const contentResponsiveContainer = style({
  margin: '0 auto',
});

const css = stylesheet({
  documentsContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    ...media({ maxWidth: BREAKPOINT_SMALL }, { alignItems: 'center' }),
  },
  headerContainer: {
    ...media({ maxWidth: BREAKPOINT_SMALL }, { maxWidth: '100%' }),
  },
  content: {
    ...media({ maxWidth: BREAKPOINT_SMALL }, { padding: 0 }),
  },
  containerInner: {
    overflow: 'auto',
    flex: '1 1 auto',
    // fix for issue with boxshadow being clipped
    padding: '20px 8px 20px',
    marginTop: -20,
    paddingBottom: 32,
    ...media({ maxWidth: BREAKPOINT_SMALL }, { ...MOBILE_CONTAINER_PADDINGS, maxWidth: '100%' }),
  },
  headerButtonsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    ...media({ maxWidth: BREAKPOINT_SMALL }, { paddingTop: 8, width: '100%' }),
  },
  selectInput: {
    width: 260,
    flexShrink: 0,
    ...media({ maxWidth: BREAKPOINT_SMALL }, { width: '100%', flex: '1 1 100%' }),
  },
  loaderContainer: {
    height: 300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loader: {
    width: 48,
    height: 48,
  },
  needAttention: {
    padding: 24,
    borderRadius: 24,
    backgroundColor: Gray10,
    marginBottom: 24,
  },
  needAttentionSubtitle: {
    color: Gray100,
  },
  cardsContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    columnGap: 12,
    rowGap: 24,
    padding: '0 24px',
    ...media(
      { maxWidth: BREAKPOINT_SMALL },
      {
        padding: 0,
        justifyContent: 'center',
        $nest: {
          '>*:not(:last-child)': {
            marginBottom: 24,
          },
        },
      },
    ),
  },
});

export default DocumentsWindow;
